/**
 * Phase F3 — Provider Dashboard
 * ------------------------------
 * Single-page summary of the provider's assignments + metrics.
 *
 * Endpoints used:
 *   GET /api/provider/dashboard                   — metrics + assignments + pending requests
 *   GET /api/providers/{provider_id}/metrics      — fresh metrics document
 *
 * Pure UI. No backend logic modified.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LayoutDashboard, CheckCircle, Activity, Briefcase, Star, TrendingUp,
  ListChecks, Award,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 20, boxShadow: theme.colors.shadows.sm,
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

function MetricCard({ Icon, label, value, color, testid }) {
  return (
    <div data-testid={testid} style={{ ...cardStyle, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: theme.radius.sm,
          background: `${color}15`, color, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={18} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {label}
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: theme.colors.text.primary, marginTop: 2 }}>
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

const StatusPill = ({ status }) => {
  const color =
    status === 'ACTIVE' ? theme.colors.status.success :
    status === 'ACCEPTED' ? theme.colors.status.info :
    status === 'COMPLETED' ? theme.colors.accent.teal :
    status === 'PROPOSED' ? theme.colors.status.warning :
    status === 'DECLINED' || status === 'TERMINATED' ? theme.colors.status.error :
    theme.colors.text.muted;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 999,
      background: `${color}1A`, color,
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
    }}>{status || '—'}</span>
  );
};

function AssignmentRow({ a }) {
  return (
    <tr data-testid={`assignment-row-${a.id}`} style={{ borderTop: `1px solid ${theme.colors.borders}` }}>
      <td style={{ padding: '10px 12px', fontWeight: 600, color: theme.colors.text.primary }}>
        {a.portfolio_id}
      </td>
      <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>{a.role_in_portfolio || '—'}</td>
      <td style={{ padding: '10px 12px' }}>
        {a.fee_type === 'FLAT' ? `$${a.fee_flat ?? 0}` : `${a.fee_percent ?? 0}%`}
      </td>
      <td style={{ padding: '10px 12px' }}><StatusPill status={a.status} /></td>
      <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>{fmtDate(a.created_at)}</td>
    </tr>
  );
}

export default function ProviderDashboardF3({ user }) {
  const [data, setData] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dash = await axios.get(`${API}/provider/dashboard`, auth);
        if (cancelled) return;
        setData(dash.data || null);
        const pid = dash.data?.provider_id;
        if (pid) {
          const m = await axios.get(`${API}/providers/${pid}/metrics`, auth).catch(() => null);
          if (!cancelled) setMetrics(m?.data?.metrics || null);
        }
      } catch (e) {
        if (!cancelled) toast.error('Failed to load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [auth]);

  const m = metrics || data?.metrics || {};
  const active = data?.active_assignments || [];
  const completed = data?.completed_assignments || [];
  const allAssignments = data?.assignments || [];

  return (
    <div data-testid="provider-dashboard-page" style={{ padding: 24, maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 800, color: theme.colors.text.primary, margin: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <LayoutDashboard size={26} color={theme.colors.primary} />
          Provider Dashboard
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginTop: 4 }}>
          Performance snapshot, active engagements, and pending opportunities.
        </p>
      </div>

      {/* Metrics row */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18,
      }}>
        <MetricCard testid="metric-active" Icon={Activity} label="Active" value={loading ? '…' : (m.active_count ?? active.length)} color={theme.colors.status.success} />
        <MetricCard testid="metric-completed" Icon={CheckCircle} label="Completed" value={loading ? '…' : (m.completed_count ?? completed.length)} color={theme.colors.accent.teal} />
        <MetricCard testid="metric-served" Icon={Briefcase} label="Total Served" value={loading ? '…' : (m.portfolios_served ?? allAssignments.length)} color={theme.colors.primary} />
        <MetricCard testid="metric-pending" Icon={ListChecks} label="Pending Requests" value={loading ? '…' : (data?.pending_request_count ?? 0)} color={theme.colors.status.warning} />
        <MetricCard testid="metric-rating" Icon={Star} label="Rating" value={loading ? '…' : (m.average_rating != null ? Number(m.average_rating).toFixed(1) : '—')} color={theme.colors.accent.salmon} />
        <MetricCard testid="metric-success-rate" Icon={TrendingUp} label="Success %" value={loading ? '…' : (m.success_rate != null ? `${Math.round(Number(m.success_rate) * 100)}%` : '—')} color={theme.colors.status.info} />
      </div>

      {/* Active assignments */}
      <div style={{ ...cardStyle, padding: 0, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${theme.colors.borders}`,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Activity size={16} color={theme.colors.status.success} />
          <div style={{ fontWeight: 700, color: theme.colors.text.primary }}>Active Assignments</div>
          <span style={{
            marginLeft: 'auto', padding: '2px 10px', borderRadius: 999,
            background: theme.colors.bg.tertiary, fontSize: 11, fontWeight: 700,
            color: theme.colors.text.secondary,
          }}>{active.length}</span>
        </div>
        {active.length === 0 ? (
          <div style={{ padding: 24, color: theme.colors.text.muted, textAlign: 'center' }} data-testid="active-empty">
            No active assignments.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: theme.colors.bg.secondary }}>
              <tr>
                {['Portfolio', 'Role', 'Fee', 'Status', 'Created'].map((h) => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '10px 12px',
                    fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                    color: theme.colors.text.muted, fontWeight: 700,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>{active.map((a) => <AssignmentRow key={a.id} a={a} />)}</tbody>
          </table>
        )}
      </div>

      {/* Completed assignments */}
      <div style={{ ...cardStyle, padding: 0, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${theme.colors.borders}`,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <CheckCircle size={16} color={theme.colors.accent.teal} />
          <div style={{ fontWeight: 700, color: theme.colors.text.primary }}>Completed Assignments</div>
          <span style={{
            marginLeft: 'auto', padding: '2px 10px', borderRadius: 999,
            background: theme.colors.bg.tertiary, fontSize: 11, fontWeight: 700,
            color: theme.colors.text.secondary,
          }}>{completed.length}</span>
        </div>
        {completed.length === 0 ? (
          <div style={{ padding: 24, color: theme.colors.text.muted, textAlign: 'center' }} data-testid="completed-empty">
            No completed assignments yet.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: theme.colors.bg.secondary }}>
              <tr>
                {['Portfolio', 'Role', 'Fee', 'Status', 'Created'].map((h) => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '10px 12px',
                    fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                    color: theme.colors.text.muted, fontWeight: 700,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>{completed.map((a) => <AssignmentRow key={a.id} a={a} />)}</tbody>
          </table>
        )}
      </div>

      {/* Portfolio assignments (all) */}
      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${theme.colors.borders}`,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Award size={16} color={theme.colors.primary} />
          <div style={{ fontWeight: 700, color: theme.colors.text.primary }}>All Portfolio Assignments</div>
          <span style={{
            marginLeft: 'auto', padding: '2px 10px', borderRadius: 999,
            background: theme.colors.bg.tertiary, fontSize: 11, fontWeight: 700,
            color: theme.colors.text.secondary,
          }}>{allAssignments.length}</span>
        </div>
        {allAssignments.length === 0 ? (
          <div style={{ padding: 24, color: theme.colors.text.muted, textAlign: 'center' }} data-testid="all-assignments-empty">
            No portfolio assignments.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: theme.colors.bg.secondary }}>
              <tr>
                {['Portfolio', 'Role', 'Fee', 'Status', 'Created'].map((h) => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '10px 12px',
                    fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                    color: theme.colors.text.muted, fontWeight: 700,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>{allAssignments.map((a) => <AssignmentRow key={a.id} a={a} />)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
